/**
 * DataImport-Xlsx-Parser — a host-side Excel parser for the pict-section-dataimport wizard.
 *
 * Why this exists: the Headlight build EXCLUDES `xlsx` from browserify (`.gulpfile-quackage-config.json`
 * BrowserifyIgnore) and loads SheetJS as a global (`window.XLSX`, `html/index.html`) — bundling the Node-only
 * paths of xlsx@0.18.5 breaks module evaluation. The stock module parser only does `require('xlsx')`, which
 * under `--ignore` returns an EMPTY STUB `{}` (it does not throw), so `XLSX.read(...)` blows up with
 * "read is not a function" inside the detect promise → the wizard silently renders an empty step and Excel
 * "fails outright". This subclass fixes it exactly the way the app's legacy importer does
 * (ContractProjectTypesImportProvider `_getXlsx`): prefer `window.XLSX`, fall back to `require`, and fail
 * with a clear message when neither is a real SheetJS. Registered via the wizard's `Parsers` seam
 * (DataImport-Provider), so no module edit / republish is needed.
 */

const libPictSectionDataImport = require('pict-section-dataimport');

class DataImportXlsxParser extends libPictSectionDataImport.ParserXlsx
{
	/**
	 * Resolve SheetJS: the browser global first (how Headlight ships it), then the optional npm dep, and
	 * throw a clear error if what we got isn't a real SheetJS (guards the browserify-ignore stub `{}`).
	 * @return {any}
	 */
	_xlsx()
	{
		if (this._xlsxLib) { return this._xlsxLib; }
		if ((typeof window !== 'undefined') && window.XLSX) { this._xlsxLib = window.XLSX; }
		else { try { this._xlsxLib = require('xlsx'); } catch (pError) { this._xlsxLib = null; } }
		if (!this._xlsxLib || (typeof this._xlsxLib.read !== 'function'))
		{
			throw new Error('DataImport: the xlsx (SheetJS) library is not available — it should load as window.XLSX (js/xlsx.full.min.js).');
		}
		return this._xlsxLib;
	}

	/**
	 * Read the chosen worksheet as an array-of-arrays. Overridden to (a) resolve xlsx + read the buffer
	 * INSIDE the promise so any failure rejects (rather than throwing synchronously out of detect()/parse()
	 * and bypassing the wizard's .catch), and (b) wrap the buffer as a Uint8Array so SheetJS's
	 * `instanceof ArrayBuffer` check holds across bundle/iframe realms.
	 * @param {Record<string, any>} pFileHandle
	 * @param {Record<string, any>} pParseConfig
	 * @return {Promise<Array<Array<any>>>}
	 */
	_readSheetMatrix(pFileHandle, pParseConfig)
	{
		return this._readArrayBuffer(pFileHandle).then((pBuffer) =>
		{
			const tmpXLSX = this._xlsx();
			const tmpWorkbook = tmpXLSX.read(new Uint8Array(pBuffer), { type: 'array' });
			const tmpSheetName = (pParseConfig && pParseConfig.Xlsx && pParseConfig.Xlsx.SheetName) || tmpWorkbook.SheetNames[0];
			const tmpSheet = tmpWorkbook.Sheets[tmpSheetName];
			if (!tmpSheet) { return []; }
			return tmpXLSX.utils.sheet_to_json(tmpSheet, { header: 1, defval: '', blankrows: false });
		});
	}

	/**
	 * The worksheet/tab names in an uploaded workbook — used by the bespoke Contract-package import to treat
	 * a multi-tab workbook as one dataset per entity.
	 * @param {Record<string, any>} pFileHandle
	 * @return {Promise<Array<string>>}
	 */
	listSheetNames(pFileHandle)
	{
		return this._readArrayBuffer(pFileHandle).then((pBuffer) =>
		{
			const tmpXLSX = this._xlsx();
			const tmpWorkbook = tmpXLSX.read(new Uint8Array(pBuffer), { type: 'array' });
			return tmpWorkbook.SheetNames || [];
		});
	}
}

module.exports = DataImportXlsxParser;
